import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Wallet, Download, Check } from 'lucide-react';
import { fmtUSD, todayISO } from './utils';
import { API_BASE } from '../../utils/apiBase';
import ReceiptScanner from './ReceiptScanner';
import logger from '../../utils/logger';

const EMPTY = { date: todayISO(), category: 'Other', description: '', amount: '', payment_method: 'Card', vendor: '', receipt_url: '', notes: '', payment_date: '' };

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

export default function ExpensesTab({ api, onRefresh }) {
  const [list, setList] = useState([]);
  const [config, setConfig] = useState({ expense_categories: [] });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchAll = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([api.get('/finance/expenses'), api.get('/finance/config')]);
      setList(l.data); setConfig(c.data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...EMPTY, ...e }); setDialog(true); };

  const save = async () => {
    try {
      if (!form.amount) { toast.error('Amount required'); return; }
      if (editing) { await api.put(`/finance/expenses/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/finance/expenses', form); toast.success('Logged'); }
      setDialog(false); fetchAll(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await api.delete(`/finance/expenses/${id}`); fetchAll(); onRefresh?.();
  };

  const markPaid = async (e) => {
    try {
      await api.put(`/finance/expenses/${e.id}`, { payment_date: todayISO() });
      toast.success('Marked paid');
      fetchAll(); onRefresh?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const monthStart = todayISO().slice(0, 7);
  const mtdTotal = list.filter(e => (e.date || '').startsWith(monthStart)).reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Card className="border-[#AD1457] bg-[#FCE4EC]"><CardContent className="p-3"><p className="text-[10px] uppercase tracking-wider text-[#AD1457] font-semibold">MTD Total</p><p className="text-lg font-bold text-[#2D1B4E]">{fmtUSD(mtdTotal)}</p></CardContent></Card>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/api/finance/export/expenses`, '_blank')} className="border-[#DED8EB] gap-1 h-9" data-testid="exp-export-btn"><Download className="h-3.5 w-3.5" /> CSV</Button>
          <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-9" data-testid="exp-add-btn"><Plus className="h-4 w-4" /> Log Expense</Button>
        </div>
      </div>

      {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : list.length === 0 ? (
        <div className="text-center py-10 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <Wallet className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No expenses logged.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="exp-list">
          {list.map(e => (
            <Card key={e.id} className={`border shadow-sm bg-white ${e.status === 'pending' ? 'border-l-4 border-l-amber-300' : ''}`} data-testid={`exp-row-${e.id}`}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-[#2D1B4E]">{e.description || e.category}</h3>
                      <Badge className={`text-[10px] border ${STATUS_STYLES[e.status || 'pending']}`}>{e.status || 'pending'}</Badge>
                      <Badge className="text-[10px] bg-[#F0EDF6] text-[#6B3FA0] border-[#DED8EB]">{e.category}</Badge>
                      <span className="text-[10px] text-[#8E85A0]">{e.date}</span>
                      {e.payment_date && <span className="text-[10px] text-emerald-700">· paid {e.payment_date}</span>}
                      {e.payment_method && <span className="text-[10px] text-[#8E85A0] italic">· {e.payment_method}</span>}
                    </div>
                    <p className="text-[11px] text-[#8E85A0] mt-0.5">{e.vendor} {e.created_by_name ? `· by ${e.created_by_name}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(e.amount)}</p>
                    {e.status !== 'paid' && (
                      <Button size="icon" variant="ghost" onClick={() => markPaid(e)} title="Mark paid" className="h-7 w-7 text-[#1B5E20] hover:bg-[#E8F5E9]" data-testid={`exp-pay-${e.id}`}><Check className="h-4 w-4" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => openEdit(e)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`exp-edit-${e.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(e.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`exp-del-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="exp-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Expense' : 'Log Expense'}</DialogTitle></DialogHeader>
          {!editing && (
            <div className="pb-2 border-b border-[#F0EDF6]">
              <ReceiptScanner api={api} kind="expense" onExtracted={(f) => {
                setForm(prev => ({
                  ...prev,
                  vendor: f.vendor || prev.vendor,
                  amount: f.amount ?? prev.amount,
                  date: f.date || prev.date,
                  category: f.category || prev.category,
                  description: f.description || prev.description,
                  payment_method: f.payment_method || prev.payment_method,
                }));
              }} />
            </div>
          )}
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="border-[#DED8EB]" data-testid="exp-date" /></div>
              <div><Label>Amount ($)</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} className="border-[#DED8EB]" data-testid="exp-amount" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="border-[#DED8EB]" data-testid="exp-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{(config.expense_categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Payment Method</Label><Input value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))} className="border-[#DED8EB]" data-testid="exp-method" /></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="border-[#DED8EB]" data-testid="exp-desc" /></div>
            <div><Label>Vendor / Merchant</Label><Input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} className="border-[#DED8EB]" data-testid="exp-vendor" /></div>
            <div><Label>Receipt URL</Label><Input value={form.receipt_url} onChange={e => setForm(f => ({ ...f, receipt_url: e.target.value }))} placeholder="https://…" className="border-[#DED8EB]" data-testid="exp-receipt" /></div>
            <div><Label>Payment Date <span className="text-[10px] text-[#8E85A0] font-normal">(leave blank if not paid yet)</span></Label><Input type="date" value={form.payment_date || ''} onChange={e => setForm(f => ({ ...f, payment_date: e.target.value }))} className="border-[#DED8EB]" data-testid="exp-pay-date" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white" data-testid="exp-save-btn">{editing ? 'Update' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
